import React, { ReactElement } from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';

import {
  gray40,
  gray80,
  primaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { boldMd, regularLg, regularXl } from '../../../constants/location';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';
import { suspendInvitedLabels } from '../../../constants/suspendInvited';

type Props = {
  open: boolean;
  title: string;
  body: string;
  /** 確定ボタンの文言（省略時「変更する」） */
  confirmLabel?: string;
  /** 確定ボタン処理中の文言（省略時「変更中...」） */
  processingLabel?: string;
  /** 処理中フラグ（確定ボタン文言が processingLabel になり、操作不可になる） */
  processing?: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const styles = {
  buttonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '32px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
      marginTop: '20px',
    },
  },
  cancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 4px)',
    },
  },
  cancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  confirmButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
  },
  confirmButtonText: {
    ...boldMd,
    color: 'white',
  },
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      '@media screen and (max-width: 960px)': {
        padding: '32px 16px',
      },
    },
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
    '@media screen and (max-width:960px)': {
      marginBottom: '16px',
    },
  },
  icon: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  iconButton: {
    padding: 0,
  },
  title: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
};

/**
 * ステータス変更の確認ダイアログ (#2802)
 * 企業（契約ステータス）/ ユーザー（登録ステータス）両方で利用する共通コンポーネント。
 */
const StatusChangeConfirmDialog = ({
  open,
  title,
  body,
  confirmLabel,
  processingLabel,
  processing = false,
  onClose,
  onConfirm,
}: Props): ReactElement => {
  const proxyDialogSx = useProxyDialogSx();

  return (
    <Dialog open={open} sx={[styles.dialog, proxyDialogSx]}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>{title}</Typography>
        <IconButton onClick={onClose} sx={styles.iconButton} disabled={processing}>
          <Close sx={styles.icon} />
        </IconButton>
      </Box>
      {/* 複数行の文面を渡せるよう改行を反映する（#3262） */}
      <Typography sx={{ whiteSpace: 'pre-line' }}>{body}</Typography>
      <Box sx={styles.buttonWrapper}>
        <Button sx={styles.cancelButton} onClick={onClose} disabled={processing}>
          <Typography sx={styles.cancelButtonText}>{suspendInvitedLabels.cancelButton}</Typography>
        </Button>
        <Button sx={styles.confirmButton} onClick={onConfirm} disabled={processing}>
          <Typography sx={styles.confirmButtonText}>
            {processing
              ? (processingLabel ?? suspendInvitedLabels.confirmButtonProcessing)
              : (confirmLabel ?? suspendInvitedLabels.confirmButton)}
          </Typography>
        </Button>
      </Box>
    </Dialog>
  );
};

export default StatusChangeConfirmDialog;
